import { useState } from "react";
import { Button, Form, Input, Select } from "antd";
import { useNavigate } from "react-router-dom";
import {
  BulbOutlined,
  CheckCircleFilled,
  LoginOutlined,
  RightOutlined,
  TeamOutlined,
  UserAddOutlined,
} from "@ant-design/icons";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import useUser from "../../hooks/useUser";
import useData from "../../datas/useData";
import AppShell, { BloodDropIcon } from "../../components/app-shell/AppShell";
import Sheet from "../../components/app-shell/Sheet";
import { useSession } from "../../lib/session";
import "./welcome.css";

const CATEGORIES = ["Feature Request", "Bug Report", "UI/UX", "Blood Donation", "Community", "Other"];

const Welcome = () => {
  const navigate = useNavigate();
  const { openLogin } = useSession();
  const { submitSuggestion } = useUser();
  const { bloods } = useData();

  const [suggestOpen, setSuggestOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [suggestForm] = Form.useForm();

  const { mutate: sendSuggestion, isPending: suggestLoading } = useMutation({
    mutationFn: submitSuggestion,
    onSuccess: () => {
      setSubmitted(true);
      suggestForm.resetFields();
    },
    onError: () => {
      toast.error("Failed to submit. Please try again.");
    },
  });

  const closeSuggest = () => {
    setSuggestOpen(false);
    setSubmitted(false);
    suggestForm.resetFields();
  };

  return (
    <AppShell>
      <section className="home-hero">
        <span className="home-hero__pill">SSC 2007 · HSC 2009</span>
        <h1 className="home-hero__title">0709 Bangladesh</h1>
        <p className="home-hero__bn">আমরা ২০০৭ সালের SSC এবং ২০০৯ সালের HSC ব্যাচের একটি বন্ধুদের কমিউনিটি।</p>
        <p className="home-hero__en">Staying connected, donating blood, and building something meaningful together.</p>
      </section>

      <section className="home-blood">
        <div className="home-blood__head">
          <BloodDropIcon className="home-blood__icon" />
          <div>
            <h2 className="home-blood__title">Need blood?</h2>
            <p className="home-blood__sub">Tap the group you need to find donors near you.</p>
          </div>
        </div>
        <div className="home-blood__groups">
          {bloods.map(({ value }) => (
            <button
              key={value}
              type="button"
              className="home-blood__group"
              onClick={() => navigate(`/blood?blood=${encodeURIComponent(value)}`)}
            >
              {value}
            </button>
          ))}
        </div>
        <Button className="home-blood__cta" size="large" block onClick={() => navigate("/blood")}>
          Find a blood donor
        </Button>
      </section>

      <div className="app-section-title">Community</div>
      <div className="app-list">
        <button type="button" className="app-list-item" onClick={() => navigate("/app-registration")}>
          <span className="home-action-icon home-action-icon--green">
            <UserAddOutlined />
          </span>
          <span className="app-list-item__main">
            <span className="app-list-item__title">Join the community</span>
            <span className="app-list-item__sub">Register with your phone number</span>
          </span>
          <RightOutlined className="app-list-item__end" />
        </button>
        <button type="button" className="app-list-item" onClick={openLogin}>
          <span className="home-action-icon home-action-icon--blue">
            <LoginOutlined />
          </span>
          <span className="app-list-item__main">
            <span className="app-list-item__title">Member login</span>
            <span className="app-list-item__sub">Feed, chats, members and donor numbers</span>
          </span>
          <RightOutlined className="app-list-item__end" />
        </button>
        <button type="button" className="app-list-item" onClick={() => setSuggestOpen(true)}>
          <span className="home-action-icon home-action-icon--amber">
            <BulbOutlined />
          </span>
          <span className="app-list-item__main">
            <span className="app-list-item__title">Share your ideas</span>
            <span className="app-list-item__sub">Tell us how to make the app better</span>
          </span>
          <RightOutlined className="app-list-item__end" />
        </button>
      </div>

      <div className="app-section-title">Why 0709</div>
      <div className="home-features">
        <div className="home-feature">
          <BloodDropIcon className="home-feature__icon home-feature__icon--red" />
          <strong>Blood donation</strong>
          <span>Find donors by blood group and location.</span>
        </div>
        <div className="home-feature">
          <TeamOutlined className="home-feature__icon home-feature__icon--green" />
          <strong>Community</strong>
          <span>Stay connected with batchmates across Bangladesh.</span>
        </div>
      </div>

      <footer className="home-credits">
        <span>
          Founder:{" "}
          <a href="https://www.facebook.com/mamunar0709" target="_blank" rel="noreferrer">
            Mamun Ar Rashid
          </a>
        </span>
        <span>
          Developer:{" "}
          <a href="https://www.linkedin.com/in/nahidzz/" target="_blank" rel="noreferrer">
            Md Nahiduzzaman
          </a>
        </span>
      </footer>

      <Sheet open={suggestOpen} onClose={closeSuggest}>
        {submitted ? (
          <div className="sheet-form sheet-form--center">
            <CheckCircleFilled className="sheet-form__done-icon" />
            <h2 className="sheet-form__title">Thank you!</h2>
            <p className="sheet-form__hint">
              Your idea has been submitted. We read every suggestion and use them to improve the community.
            </p>
            <Button size="large" block onClick={closeSuggest}>
              Close
            </Button>
          </div>
        ) : (
          <Form form={suggestForm} layout="vertical" onFinish={sendSuggestion}>
            <h2 className="sheet-form__title" style={{ marginBottom: 12 }}>
              Share your ideas
            </h2>
            <Form.Item label="Your name (optional)" name="name">
              <Input size="large" placeholder="e.g. Rahim, or leave blank" />
            </Form.Item>
            <Form.Item label="Category" name="category" initialValue="Feature Request">
              <Select size="large" options={CATEGORIES.map((c) => ({ label: c, value: c }))} />
            </Form.Item>
            <Form.Item
              label="Your suggestion"
              name="message"
              rules={[{ required: true, message: "Please write your suggestion" }]}
            >
              <Input.TextArea
                rows={4}
                placeholder="What feature would you like? How can we improve the community site?"
                maxLength={500}
                showCount
              />
            </Form.Item>
            <Button type="primary" htmlType="submit" size="large" block loading={suggestLoading}>
              Submit suggestion
            </Button>
          </Form>
        )}
      </Sheet>
    </AppShell>
  );
};

export default Welcome;
